import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  DeliveryMethod,
  zasilkovnaPickupSelectors,
} from '@/interfaces/eshop'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

const schema = z
  .object({
    zasilkovnaPickupSelector: z.enum(zasilkovnaPickupSelectors),
    zasilkovnaApiKey: z.string(),
  })
  .refine(
    (values) =>
      values.zasilkovnaPickupSelector !== 'widget' ||
      values.zasilkovnaApiKey.trim() !== '',
    {
      path: ['zasilkovnaApiKey'],
      message: 'apiKeyRequired',
    },
  )

type ZasilkovnaSettingsFormValues = z.infer<typeof schema>

interface ZasilkovnaDeliverySettingsFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  method: DeliveryMethod | null
  onSuccess: () => void
}

export function ZasilkovnaDeliverySettingsForm({
  open,
  onOpenChange,
  method,
  onSuccess,
}: ZasilkovnaDeliverySettingsFormProps) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.deliveryMethods.zasilkovnaSettings',
  })
  const { t: te } = useTranslation(undefined, {
    keyPrefix: 'eshop',
  })

  const form = useForm<ZasilkovnaSettingsFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      zasilkovnaPickupSelector: 'feed',
      zasilkovnaApiKey: '',
    },
  })

  useEffect(() => {
    if (!open || !method) return
    form.reset({
      zasilkovnaPickupSelector: method.zasilkovnaPickupSelector ?? 'feed',
      zasilkovnaApiKey: method.zasilkovnaApiKey ?? '',
    })
  }, [open, method, form])

  if (!method || method.type !== 'zasilkovna') return null

  const onSubmit = async (values: ZasilkovnaSettingsFormValues) => {
    try {
      await API.patch('/api/v2/delivery-methods/zasilkovna', {
        zasilkovnaPickupSelector: values.zasilkovnaPickupSelector,
        zasilkovnaApiKey: values.zasilkovnaApiKey,
      })
      toast.success(t('success'))
      onOpenChange(false)
      onSuccess()
    } catch (error: unknown) {
      toast.error(t('error'), {
        description: extractErrorMessage(error),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            <FormField
              control={form.control}
              name="zasilkovnaPickupSelector"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-3">
                  <FormLabel>{t('fields.selector.label')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      className="flex flex-col gap-3"
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormItem className="flex items-start space-x-2 space-y-0 rounded-lg border p-3">
                        <FormControl>
                          <RadioGroupItem value="feed" id="selector-feed" />
                        </FormControl>
                        <div className="flex flex-col gap-1">
                          <FormLabel
                            htmlFor="selector-feed"
                            className="font-medium"
                          >
                            {t('fields.selector.options.feed')}
                          </FormLabel>
                          <FormDescription>
                            {t('fields.selector.descriptions.feed')}
                          </FormDescription>
                        </div>
                      </FormItem>
                      <FormItem className="flex items-start space-x-2 space-y-0 rounded-lg border p-3">
                        <FormControl>
                          <RadioGroupItem
                            value="widget"
                            id="selector-widget"
                          />
                        </FormControl>
                        <div className="flex flex-col gap-1">
                          <FormLabel
                            htmlFor="selector-widget"
                            className="font-medium"
                          >
                            {t('fields.selector.options.widget')}
                          </FormLabel>
                          <FormDescription>
                            {t('fields.selector.descriptions.widget')}
                          </FormDescription>
                        </div>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="zasilkovnaApiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.apiKey.label')}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="off"
                      placeholder={t('fields.apiKey.placeholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>{t('fields.apiKey.help')}</FormDescription>
                  {form.formState.errors.zasilkovnaApiKey?.message ===
                    'apiKeyRequired' && (
                    <FormDescription className="text-destructive">
                      {t('fields.apiKey.required')}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {te('actions.cancel')}
              </Button>
              <Button type="submit">{te('actions.save')}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
